/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { memo } from 'preact/compat';
import { useRef, useCallback, useLayoutEffect, StateUpdater, Dispatch } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { warn } from '../utils/UNSAFE_logger';
import { allTabbableElements } from '../utils/PRIVATE_tabbableUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { ListItemRendererContext } from '../UNSAFE_Collection';
import { multiVariantStyles, styles } from './themes/BaseSwatchViewStyles.css';
import { compareListItemContext } from '../utils/PRIVATE_collectionUtils';

/**
 * Props for the BaseSwatchViewItem Component
 */
export type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  context: ListItemRendererContext<K, D>;
  isTabbable: boolean;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  focusBehavior: 'swatch' | 'content';
  setIsFocusBehaviorValid?: Dispatch<StateUpdater<boolean>>;
  updateSwatchSize?: (width: number, height: number) => void;
};

/**
 * The internal component used to render a single item in SwatchView.
 */
export function BaseSwatchViewItem<K extends string | number, D>({
  children,
  context,
  isTabbable,
  isFocused,
  isFocusRingVisible,
  focusBehavior,
  setIsFocusBehaviorValid,
  updateSwatchSize
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemKey = context.metadata.key;

  const isItemFocused = isFocused && isFocusRingVisible;
  const variantClasses = multiVariantStyles({
    itemFocused: focusBehavior === 'swatch' && isItemFocused ? 'isItemFocused' : 'isNotItemFocused',
    itemInGridLayout: 'isNotInGridLayout'
  });
  const classes = classNames([styles.itemStyle, variantClasses]);

  /*********************************** Tabbable Mode ***********************************/

  // don't bubble Arrow Up/Down/Left/Right events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      focusBehavior === 'swatch' &&
      isTabbable &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      event.stopPropagation();
    }
  };

  /************************************ Swatch Size ************************************/

  const handleResize = useCallback(
    (entry: ResizeObserverEntry) => {
      updateSwatchSize?.(entry.contentRect.width, entry.contentRect.height);
    },
    [updateSwatchSize]
  );

  const nullRef = useRef<HTMLDivElement>(null);
  useResizeObserver(updateSwatchSize ? rootRef : nullRef, handleResize);

  /********************************* Focus Behavior *********************************/

  useLayoutEffect(() => {
    if (focusBehavior === 'content' && isItemFocused) {
      // rootRef is swatchview item, its first child is the focus trap div, so its first grand child
      // is the actual content
      const content = rootRef.current?.firstElementChild?.firstElementChild as HTMLElement;
      const focusable = allTabbableElements(content, true, true);
      if (focusable.length === 0 || focusable.length > 1) {
        warn(
          `${focusable.length} focusable elements are detected in the swatch. 'content' focusBehavior is intended only for use when the swatch has single focusable element. Please specify one focusable element in the swatch or switch the focusBehavior to 'swatch'.`
        );
        setIsFocusBehaviorValid?.(false);
      } else {
        focusable[0].focus();
      }
    }
  }, [focusBehavior, isItemFocused, setIsFocusBehaviorValid]);

  const childrenComponent = children(context);
  return (
    <div
      id={useId()}
      ref={rootRef}
      class={classes}
      role="gridcell"
      data-oj-key={itemKey}
      onKeyDown={handleKeyDown}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
          {childrenComponent}
        </FocusTrap>
      </TabbableModeContext.Provider>
    </div>
  );
}

export const MemoizeBaseSwatchViewItem = memo(
  BaseSwatchViewItem,
  <K, D>(prev: Props<K, D>, next: Props<K, D>) => {
    if (prev && next) {
      return (
        prev.children === next.children &&
        prev.isTabbable === next.isTabbable &&
        prev.isFocused === next.isFocused &&
        prev.isFocusRingVisible === next.isFocusRingVisible &&
        prev.focusBehavior === next.focusBehavior &&
        compareListItemContext(prev.context, next.context)
      );
    }
    return false;
  }
);
